import { debounce, later, next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { PointerEventObject } from 'highcharts';
import type Highcharts from 'highcharts';
import numeral from 'numeral';

import type { DChartInterface, Service } from 'webapp-types';
import { CompareType } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';
import { formatNumber } from 'webapp/utils/number';

import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import type VisualizationBarChart from 'webapp/models-custom/visualization/bar-chart';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import AnnotationsGridHandler from 'webapp/d-chart/annotations/grid';
import BaseRenderer from 'webapp/d-chart/renderer/base';
import DChartSeries from 'webapp/d-chart/series/series';

type GridVisualization = VisualizationLineChart | VisualizationBarChart;
type ChartWithAnnotations = Highcharts.Chart & { annotations?: Highcharts.Annotation[] };
type AnnotationtWithGraphicEl = Highcharts.Annotation & {
	graphic: Highcharts.SVGElement & { div: HTMLDivElement };
};

// a series as grid builds it, field by field: drawn as a line, spline, area, column or arearange
type GridSeriesOptions = Omit<
	Highcharts.SeriesColumnOptions,
	'type' | 'data' | 'events' | 'colorIndex'
> &
	Omit<
		Highcharts.SeriesAreaOptions,
		'type' | 'data' | 'events' | 'colorIndex' | 'borderRadius'
	> & {
		type: string;
		colorIndex?: string | number; // a class for custom colors
		data: DChartInterface.ChartDataPoint[];
		dbxSeries: DChartSeries;
		events?: Highcharts.SeriesEventsOptionsObject;
	};

// an anomaly icon annotation; groupId tells the anomaly ones apart
type AnomalyAnnotationOptions = Omit<Highcharts.AnnotationsOptions, 'events'> & {
	groupId: string;
	// mousemove / mouseout aren't in Highcharts' annotation event types
	events: Highcharts.AnnotationsEventsOptions & {
		mousemove: (this: Highcharts.Annotation, event: PointerEventObject) => void;
		mouseout: (this: Highcharts.Annotation, event: PointerEventObject) => void;
	};
};

// null means automatic, which Highcharts handles; its types only allow undefined
type GridYAxisOptions = Omit<Highcharts.YAxisOptions, 'softMin' | 'min' | 'max'> & {
	softMin?: number | null;
	min?: number | string | null;
	max?: number | string | null;
};

// columnMetrics is a Highcharts internal of column series
type SeriesWithColumnMetrics = Highcharts.Series & {
	columnMetrics?: { offset: number; width: number };
};

const ANOMALY_GROUP_ID = 'annotation-anomaly';

export default abstract class GridRenderer extends BaseRenderer {
	@service('annotations') declare annotationService: Service.Annotations;
	@service('event-manager') declare eventManagerService: Service.EventManager; // for forecast event trigger
	@service('popup') declare popupService: Service.Popup; // for annotations

	abstract get hasLegend(): boolean;

	annotationsHandler: AnnotationsGridHandler | null = null;

	// always-visible point markers on the main series (overridden per chart type)
	protected allowMarkerDotsOnVisualization = false;

	protected get showMarkerDots(): boolean {
		return this.allowMarkerDotsOnVisualization && !this.visualization.block.isSpline;
	}

	override get visualization() {
		return this.dChart.visualization as GridVisualization;
	}

	get showAnnotations() {
		// if we actually load the handler
		return this.visualization.showAnnotations;
	}

	get series() {
		return this.visualization.series || [];
	}

	get shownMainSeriesCount() {
		return this.series.filter((s) => s.isMain).length;
	}

	get showTotalsOnStacked() {
		return this.visualization.showTotalsOnStacked;
	}

	get seriesForLegend() {
		const primarySeries = this.series.filter((s) => s.isMain),
			secondarySeries = this.series.filter((s) => !s.isMain);
		return [...primarySeries, ...secondarySeries];
	}

	// fallback to forecast if no main series for cases where we are forecasting future timeframes
	get seriesForGranulationLabels() {
		return this.series.find((s) => s.isMain) || this.series.find((s) => s.isForecast) || null;
	}

	get hasBarSeries() {
		return this.series.some((s) => s.isBar);
	}

	get hasLineSeries() {
		return this.series.some((s) => !s.isBar);
	}

	get hasSecondaryAxis() {
		return this.series.some((s) => s.axisIndex === 1);
	}

	get yAxisSoftMin(): number | null {
		// when smart y scaling is enabled, we only have to pinpoint one point value
		// the hc are smart enough to go below that if such values exist.
		let somePointValue = 0;

		this.series
			.filter((s) => s.axisIndex === 0)
			.some((s) => {
				return s.points.some((p) => {
					if (typeof p.anomalyData?.lowerValue === 'number') {
						somePointValue = p.anomalyData.lowerValue;
					} else if (p.hasValue) {
						somePointValue = p.value!;
					}
				});
			});

		return !this.visualization.isYAxisFromZero && this.visualization.isYAxisMinAuto
			? somePointValue
			: this.visualization.yAxisMin || 0;
	}

	get yAxis2SoftMin(): number | null {
		// when smart y scaling is enabled, we only have to pinpoint one point value
		// the hc are smart enough to go below that if such values exist.
		let somePointValue = 0;
		this.series
			.filter((s) => s.axisIndex === 1)
			.some((s) => {
				return s.points.some((p) => {
					if (p.hasValue) {
						somePointValue = p.value!;
					}
				});
			});

		return !this.visualization.isYAxis2FromZero && this.visualization.isYAxis2MinAuto
			? somePointValue
			: this.visualization.yAxis2Min || 0;
	}

	get offsetForStackLabels() {
		const hasLineDataLabels = this.series.some(
			(s) => !s.isBar && s.isStacked && s.showDataLabels,
		);
		return hasLineDataLabels ? -1 * this.dChart.sizeModifier : 0;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;

		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				events: {
					click: function (this: Highcharts.Chart, event: PointerEventObject) {
						if (this.hoverPoint) {
							const { dbxPoint, graphic } = this
								.hoverPoint as DChartInterface.ChartHoverPoint;
							that.onPointClick(dbxPoint, event, graphic?.element);
						}
					},
					load: function (this: Highcharts.Chart) {
						that.onChartLoad(this);
					},
					render: function (this: ChartWithAnnotations, _event: Event) {
						that.onChartRender(this);
					},
				},
			},
			plotOptions: {
				series: {
					turboThreshold: 3000, // accomodate alltime daily charts (8y)
					animation: this.dChart.isAnimated,
					marker: {
						symbol: 'circle',
					},
					point: {
						events: {
							mouseOver: function (this: Highcharts.Point) {
								const annotationsHandler = that.annotationsHandler;
								if (annotationsHandler) {
									annotationsHandler.isHoveringOverPoint = true;
									annotationsHandler.showAddButton(String(this.category));
								}
							},
						},
					},
					events: {
						mouseOut: function (this: Highcharts.Series) {
							const annotationsHandler = that.annotationsHandler;
							if (annotationsHandler) {
								annotationsHandler.isHoveringOverPoint = false;
								later(() => {
									// in case we entered diff point / series
									if (!annotationsHandler.isHoveringOverPoint) {
										annotationsHandler.hideAddButton();
									}
								}, 100);
							}
						},
					},
				},
			},
			legend: {
				enabled: this.hasLegend,
			},
			xAxis: {
				labels: {
					padding: 0,
				},
				minPadding: 0.005,
				maxPadding: 0.005,
			},
			yAxis: [
				{
					title: {
						text: undefined,
					},
					labels: {
						align: 'left',
						formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
							return that.valueAxisFormatter(this);
						},
					},
				},
			],
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const that = this;

		// ---- series -----
		const series: Highcharts.SeriesOptionsType[] = [];

		const granulationLabels = this.seriesForGranulationLabels?.granulationLabels || [],
			pointIds = granulationLabels.map((item) => item.ts);

		const totalMinMax = DChartSeries.getMinAndMaxValues(
				this.series.reduce((a: (number | null)[], s) => {
					return a.concat(s.points.map((p) => p.value));
				}, []),
			),
			noValues = isNaN(totalMinMax.min) || isNaN(totalMinMax.max),
			allValuesPositive = totalMinMax.min >= 0 && totalMinMax.max >= 0,
			allValuesNegative =
				totalMinMax.min <= 0 &&
				totalMinMax.max <= 0 &&
				(!!totalMinMax.min || !!totalMinMax.max), // all zeroes does not count as all negative
			allValuesOnSameSideOfZero = noValues || allValuesPositive || allValuesNegative;

		const seriesForLegend = this.seriesForLegend;

		// filter out forecast series bcos they need extra fake arearange drawn
		const forecastSeries: DChartSeries[] = [];
		const anomalyAnnotations: AnomalyAnnotationOptions[] = [];

		const isAnomalyInSeries = !!this.series.find((s1) => s1.isAnomaly);

		this.series.forEach((s, i) => {
			const isStacked = s.isStacked,
				type = s.typeToDraw,
				colorIndex = s.colorIndexOrClass;

			const classNames = [`type-${s.type}`];
			if (isStacked) {
				classNames.push('stacked');
			}
			if (s.isTheAggregatedTotal) {
				classNames.push('aggregated');
			}

			const isYAxisFromZero =
				s.axisIndex === 1
					? this.visualization.isYAxis2FromZero
					: this.visualization.isYAxisFromZero;

			const isYAxisReversed =
				s.axisIndex === 1
					? this.visualization.isYAxis2Reversed
					: this.visualization.isYAxisReversed;

			// if true, we need to invert the gradient (the darkest part must always be closer to the line)
			// and the threshold (where lines "start") will be the top of the chart
			const isLineThresholdAbove =
				!s.isBar &&
				!s.isAvgTrendMedianOrGoal &&
				((!isYAxisReversed && allValuesNegative) || (isYAxisReversed && allValuesPositive));

			if (isLineThresholdAbove) {
				classNames.push('gradient-reverse');
			}

			if ((this.showMarkerDots || s.isSingleValue) && s.type === 0) {
				classNames.push('markers--shown');
			}

			if (s.isOneOfForecasts) {
				forecastSeries.push(s);
			}

			const legendIndex = seriesForLegend.indexOf(s);

			const sData: GridSeriesOptions = {
				type,
				className: classNames.join(' '),
				name: s.title,
				colorIndex,
				legendIndex,
				dbxSeries: s,
				yAxis: s.axisIndex || 0,
				data: [],
				dataLabels: [
					{
						enabled: s.showDataLabels,
						formatter: function (
							this: Highcharts.Point & { point?: DChartInterface.ChartHoverPoint },
						) {
							return this.point!.dbxPoint.valueStr;
						},
					},
				],
			};

			// set z-index to ensure main series renders on top of forecast series
			// this allows clicks on overlapping points to reach the main series
			if (s.isMain) {
				sData.zIndex = 99; // main series on top
			}

			s.updateDynamicThresholdFrom = null; // reset, updating in some cases below

			if (s.isBar) {
				sData.shadow = true;
				// sData.pointPadding = options.sData.length > 3 ? 0.05 : 0.1;
				// sData.maxPointWidth = 40;
				sData.centerInCategory = false; // weird bar overlapping bugs when switching from/to stacked https://github.com/highcharts/highcharts/issues/14980

				// for bar chart, "threshold" is the point where the bars "start", so for mixed negative / positive values we need to keep 0
				if (isYAxisFromZero || !allValuesOnSameSideOfZero || allValuesNegative) {
					sData.threshold = 0;
				} else {
					sData.threshold = null;
				}

				sData.index = i; // fixes https://app.asana.com/0/40713484505492/1200049075340224/f
			} else {
				if (isYAxisFromZero) {
					sData.threshold = 0;
				} else if (isLineThresholdAbove) {
					// this threshold will have to be updated to top (least negative) value on the axis, but we cant get it before rendering it
					// so the areas "start" from the positive side towards to the value line itself (important for gradient)
					sData.threshold = null; // temporary - see updateDynamicElementsOnRender
					s.updateDynamicThresholdFrom = isYAxisReversed ? 'min' : 'max';
				} else if (allValuesNegative && isYAxisReversed) {
					// in this case, we want gradient below the line, so we do not have gradient-reverse class
					// but with threshold null (for auto) highcharts sets by default above (bcos of negatives), so we need to override it
					s.updateDynamicThresholdFrom = 'max';
					sData.threshold = null;
				} else {
					sData.threshold = null; // auto
				}
			}

			if (s.isBar) {
				sData.marker = {
					enabled: false,
				};
			} else {
				if (!isStacked && s.isSingleValue) {
					// single point must show regardless of chart size (chart-wide marker
					// default is size-gated and would hide it on small charts)
					sData.marker = {
						enabled: true,
					};
				} else {
					// only set cases where we definitely disable or definitely enable
					// the rest are left for global flag depending on block size (setSizeOptions)
					const showMarkerDotsForSeries =
						this.showMarkerDots && s.isMain && s.points.length <= 31;
					if (!showMarkerDotsForSeries) {
						sData.marker = {
							enabled: false,
						};
					}
				}
			}

			// used to connect series with the hc series obj
			sData.id = s.idForHc = `${s.baseId}-index-${i}`;

			let textLabel = '';
			if (s.isTrend) {
				textLabel = 'Trend';
			} else if (s.isAverage) {
				textLabel = 'Average';
			} else if (s.isMedian) {
				textLabel = 'Median';
			} else if (s.isGoal) {
				textLabel = s.titlePrefix;
			}

			if (isStacked) {
				sData.stack = s.stackId;
				sData.stacking = 'normal';
			} else {
				sData.stacking = undefined;
				sData.stack = `random-${Math.random()}`; // "resetting" stacked series
			}

			if (s.isBar) {
				sData.borderRadius = {
					radius: 8,
					scope: isStacked ? 'stack' : 'point',
				} as Highcharts.BorderRadiusOptionsObject; // `where` has a default, but the types require it
			}

			if (!s.hasTooltip) {
				sData.enableMouseTracking = false;
			}

			const firstPointWithValueIndex = s.points.findIndex((point) => point.hasValue);

			const reversedIndex = s.points
				.slice()
				.reverse()
				.findIndex((point) => point.hasValue);
			const lastPointWithValueIndex =
				reversedIndex !== -1 ? s.points.length - 1 - reversedIndex : -1;

			// data points
			const pointsData = s.points.map((point, idx) => {
				const props: DChartInterface.ChartDataPoint = {
					y: point.value,
					colorIndex, // also specify here because it might not update it on all points when updating on series (https://app.asana.com/0/1199990233377000/1199921114592922/f)
					dbxPoint: point,
				};

				if (s.isAnomaly) {
					Object.assign(props, {
						low: point.anomalyData?.lowerValue,
						high: point.anomalyData?.upperValue,
						x: idx,
					});
				}

				const isFirst = !idx || firstPointWithValueIndex === idx,
					isLast = s.points.length === idx + 1 || lastPointWithValueIndex === idx;

				if (isFirst) {
					props.className = 'first-point';
				}

				if (isLast) {
					props.className = 'last-point';
					if (textLabel) {
						// last point - show text label on the avg, goal, trend lines
						props.dataLabels = {
							enabled: true,
							format: textLabel,
						};
					}
				}

				const anomalyValue = point.comparePoints.find(
					(p) => p.compareType === CompareType.ANOMALY,
				)?.value;

				if (point.hasValue && point.anomalyData && anomalyValue) {
					props.marker = {
						enabled: true,
						radius: 4,
						states: {
							hover: {
								radius: 4,
							},
						},
					};

					props.id = `anomaly-icon-${idx}`;
					props.className =
						(!s.isBar && `anomaly-point ${point.anomalyData.name}`) || undefined;
					const className = `anomaly-annotations ${point.anomalyData.name}`;

					const toggleAnomalyHover = (chart: Highcharts.Chart, on: boolean) => {
						const annotation = (chart as ChartWithAnnotations).annotations?.find(
							(a) => a.options.id === `anomaly-annotation-${idx}`,
						) as AnnotationtWithGraphicEl | undefined;
						annotation?.graphic.div
							.querySelector('svg')
							?.classList.toggle('active', on);
					};

					props.events = {
						mouseOver: function () {
							toggleAnomalyHover(this.series.chart, true);
						},
						mouseOut: function () {
							toggleAnomalyHover(this.series.chart, false);
						},
					};

					const updateAnomalyBarPosition = this.updateAnomalyBarPosition.bind(this);

					anomalyAnnotations.push({
						draggable: '',
						id: `anomaly-annotation-${idx}`,
						groupId: ANOMALY_GROUP_ID,
						events: {
							mousemove: function (
								this: Highcharts.Annotation,
								event: PointerEventObject,
							) {
								event.stopPropagation();
								this.chart.series[i]!.points[idx]!.onMouseOver(event);
								const target = event.target as SVGUseElement;
								target.classList.add('active');
							},
							mouseout: function (
								this: Highcharts.Annotation,
								event: PointerEventObject,
							) {
								this.chart.series[i]!.points[idx]!.onMouseOut();
								const target = event.target as SVGUseElement;
								target.classList.remove('active');
							},
							add: function (that?: object) {
								// the event's target is the added annotation
								updateAnomalyBarPosition(
									this.chart,
									(that as { target: AnnotationtWithGraphicEl }).target.graphic,
								);
							},
						},
						labelOptions: {
							allowOverlap: true,
							useHTML: true,
							text: `<div class="${className}"><svg width="26" height="40"><use href="#inline-svg--anomaly-icon"></use></svg></div>`,
							padding: 0,
							distance: s.isBar ? 8 : 10,
							shape: 'rect',
						},
						labels: [{ point: { x: idx, y: point.value!, yAxis: 0, xAxis: 0 } }], // point.hasValue
					});
				}

				if (point.onClick) {
					const that = this;
					props.className = props.className
						? `${props.className} point-clickable`
						: 'point-clickable';
					props.events = {
						click: function (this: Highcharts.Point, event: PointerEventObject) {
							that.onPointClick(point, event, this.graphic?.element);
						},
					};
				}

				return props;
			});

			if (s.isAnomaly) {
				sData.enableMouseTracking = false;
			}

			if (isAnomalyInSeries) {
				if (s.isMain) {
					if (sData.type !== 'column') {
						sData.type = 'spline';
					}

					sData.events = {
						afterAnimate: function () {
							next(() => {
								const chart = this.chart;
								chart.update({ chart: { marginTop: 50 } }); // Add offset on top of the chart
							});
						},
						show: function () {
							const chart = this.chart as ChartWithAnnotations;

							anomalyAnnotations.forEach((a) => {
								chart.addAnnotation(a);
							});
						},
						hide: function () {
							const chart = this.chart as ChartWithAnnotations;
							const annotations = chart.annotations?.filter(
								(a) =>
									(a.options as AnomalyAnnotationOptions).groupId ===
									ANOMALY_GROUP_ID,
							);
							annotations?.forEach((a: Highcharts.Annotation) => {
								chart.removeAnnotation(a);
							});
						},
					};
				} else {
					const setAnomalyAnnotationVisibility = (chart: ChartWithAnnotations) => {
						(chart.annotations as AnnotationtWithGraphicEl[])?.forEach((a) => {
							this.updateAnomalyBarPosition(chart, a.graphic);
						});
					};

					sData.events = {
						show: function () {
							setAnomalyAnnotationVisibility(this.chart);
						},
						hide: function () {
							setAnomalyAnnotationVisibility(this.chart);
						},
					};
				}
			}

			sData.data = pointsData;

			// ---- zones - for or incomplete or missing values
			// example https://jsfiddle.net/vx45n1Lj/
			const isColumn = sData.type === 'column',
				zones = [];

			let prevZoneType = '';
			sData.data.forEach((item: DChartInterface.ChartDataPoint, j: number) => {
				const isMissing = typeof item.y !== 'number' || item.dbxPoint.incomplete,
					zoneType = isMissing ? 'missing' : '';

				if (j) {
					if (prevZoneType !== zoneType) {
						// ON NON-BAR CHARTS: if this is where the missing starts, also mark previous connection as missing
						// ON ALL: if this is where the solid starts, start it at this point forward
						const endIndex = isMissing && !isColumn ? j - 1 : j;
						zones.push({
							value: endIndex,
							className: prevZoneType ? `zonetype-${prevZoneType}` : '',
						});
					}
				}

				prevZoneType = zoneType;
			});
			if (prevZoneType) {
				zones.push({
					value: isColumn ? sData.data.length : sData.data.length - 1,
					className: prevZoneType ? `zonetype-${prevZoneType}` : '',
				});
			}

			sData.zones = zones;
			sData.connectNulls = true;
			sData.zoneAxis = 'x';

			series.push(sData as Highcharts.SeriesOptionsType);
		});

		if (forecastSeries.length) {
			// drawing fake arearanges for optimistic / pessimistic forecasts
			const mainForecastSeries = this.series.find((s) => s.isForecast) || null,
				optimisticForecast = this.series.find((s) => s.isForecastOptimistic) || null,
				pessimisticForecast = this.series.find((s) => s.isForecastPessimistic) || null;
			if (mainForecastSeries) {
				const getSeriesDataForSeries = (s: DChartSeries) => {
					const id = `forecast-${s.type}--passive`;
					return {
						id,
						type: 'arearange',
						className: id,
						name: '',
						enableMouseTracking: false,
						dbxSeries: s,
						yAxis: s.axisIndex || 0,
						linkedTo: s.idForHc, // for joint legend
						connectNulls: true,
						marker: { enabled: false },
						data: s.points.map((point, idx) => {
							const otherPoint = mainForecastSeries.points.find(
								(p) => p.index === idx,
							);
							return {
								dbxPoint: point,
								low: point.value ?? undefined,
								high: otherPoint?.value ?? undefined,
							};
						}),
					} as Highcharts.SeriesOptionsType;
				};

				if (optimisticForecast) {
					series.push(getSeriesDataForSeries(optimisticForecast));
				}
				if (pessimisticForecast) {
					series.push(getSeriesDataForSeries(pessimisticForecast));
				}
			}
		}

		// ---- annotations (icons above x axis labels) -----
		let annotations: Highcharts.AnnotationsOptions[] = [];
		if (this.annotationsHandler) {
			annotations = this.annotationsHandler.prepareAnnotationsData();
		}
		// ---- x axis -----

		const axisLabels = granulationLabels.map(
			(granulationData) => granulationData.labelShort || granulationData.label,
		);

		const xAxis = {
			// the points' timestamps; the labels formatter shows axisLabels instead
			categories: pointIds.map(String),
			crosshair: this.hasBarSeries ? false : { width: 1, snap: true, zIndex: 100 },
			labels: {
				formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
					return axisLabels[this.pos]!; // pos is a category's index
				},
			},
			tickInterval: 1,
			// dataMax and width are Highcharts internals, set by the time it calls the positioner
			tickPositioner: function (
				this: Highcharts.Axis & { dataMax?: number; width?: number },
			) {
				const pos = this.tickPositions!;

				let adjustedPos: number[] = [];
				// for axis with 7 points or less, keep everything
				if (this.dataMax! < 7) {
					adjustedPos = pos;
				} else {
					const maxTickIndex = this.dataMax!, // 0-index based
						labelSpace = 25, // Math.max(40, this.maxLabelLength + 20), // bcos maxLabelLength on Axis does not exist anymore?
						maxTicks = Math.floor(this.width! / labelSpace) + 1,
						minTicks = Math.max(2, Math.floor(this.width! / 100)) + 1;

					// find "optimal" tick count (the one that leaves the least unlabeled items at the end)
					let optimalTicks = minTicks,
						diff = Infinity;
					for (let t = minTicks - 1; t <= maxTicks - 1; t++) {
						const tickDiff = maxTickIndex % t;
						if (tickDiff <= diff) {
							diff = tickDiff;
							optimalTicks = t;
						}
					}

					// showing optimalTicks + 1 positions (because we want to include first and last)
					const everyX = Math.floor(maxTickIndex / optimalTicks);
					for (let i = 0; i <= maxTickIndex; i++) {
						if (!(i % everyX)) {
							adjustedPos.push(i);
						}
					}
				}

				return adjustedPos;
			},
		};

		// ---- y axis -----

		const stackLabels = {
			enabled: this.showTotalsOnStacked,
			allowOverlap: true,
			crop: false,
			overflow: 'allow' as const,
			y: this.offsetForStackLabels,
			formatter: function (this: Highcharts.StackItemObject) {
				return that.formatStackTotal(this.total);
			},
		};

		const yAxesOptions: GridYAxisOptions[] = [
			{
				softMin: this.yAxisSoftMin,
				min: this.visualization.yAxisMin,
				max: this.visualization.yAxisMax,
				startOnTick: !this.visualization.yAxisMin,
				endOnTick: !this.visualization.yAxisMax,
				reversed: this.visualization.isYAxisReversed,
				stackLabels,
				plotLines: [
					{
						value: 0,
						className: 'highcharts-zero-line',
						zIndex: 1,
					},
				],
			},
		];

		if (this.hasSecondaryAxis) {
			// also include base options
			const yAxis2BaseOpts =
				((this.baseOptions.yAxis as Highcharts.YAxisOptions[] | undefined) || [])[0] || {};

			const yAxis2 = this.mergeOptions<GridYAxisOptions>(yAxis2BaseOpts, yAxesOptions[0]!, {
				opposite: true,
				softMin: this.yAxis2SoftMin,
				min: this.visualization.yAxis2Min,
				max: this.visualization.yAxis2Max,
				startOnTick: !this.visualization.yAxis2Min,
				endOnTick: !this.visualization.yAxis2Max,
				reversed: this.visualization.isYAxis2Reversed,
				stackLabels,
			});
			yAxis2.labels!.align = 'right';
			yAxesOptions.push(yAxis2);
		}

		// ----- other props -----

		const shownMainSeriesCount = this.series.filter((s) => s.isMain).length;
		let chartClassName = '';
		if (shownMainSeriesCount < 2) {
			chartClassName = 'main-count--1';
		} else if (shownMainSeriesCount === 2) {
			chartClassName = 'main-count--2';
		} else {
			chartClassName = 'main-count--many';
		}

		if (this.hasLineSeries && this.hasBarSeries) {
			// mixed - will remove line gradients etc (see css)
			chartClassName += ' mixed';
		}

		const plotOptions: { series: Highcharts.PlotSeriesOptions } = {
			series: {},
		};

		if (this.hasBarSeries) {
			plotOptions.series.pointPlacement = 0;
		} else {
			plotOptions.series.pointPlacement = 'on';
		}

		// merging with sizeOptions because data options influence size (this.hasSecondaryAxis)
		return this.mergeOptions(this.sizeOptions, {
			chart: {
				className: chartClassName,
			},
			legend: {
				enabled: this.hasLegend,
			},
			xAxis,
			yAxis: yAxesOptions as Highcharts.YAxisOptions[], // with null for auto bounds
			plotOptions,
			series,
			annotations: [...annotations, ...anomalyAnnotations],
		});
	}

	override get sizeOptions(): Highcharts.Options {
		// its yAxis is an array (see baseOptions), pushed to below
		let options = super.sizeOptions as Highcharts.Options & {
			yAxis: Highcharts.YAxisOptions[];
		};

		const m = this.dChart.sizeModifier;

		const chartMarginLeftRight = 1.3 * m, // same as .block left/right padding
			yAxisLabelSpace = 1.7 * m,
			hasSecondaryAxis = this.hasSecondaryAxis;

		options = this.mergeOptions(options, {
			chart: {
				spacingTop: m / 2, // to not cut off the top label
				marginLeft: chartMarginLeftRight + yAxisLabelSpace,
				marginRight: chartMarginLeftRight + (hasSecondaryAxis ? yAxisLabelSpace : 0),
			},
			xAxis: {
				tickLength: m / 2,
				labels: {
					y: m + m * AnnotationsGridHandler.HEIGHT,
				},
			},
			yAxis: [
				{
					labels: {
						padding: m / 3,
						x: -0.5 * m - yAxisLabelSpace,
						align: 'left',
					},
					stackLabels: {
						y: this.offsetForStackLabels,
					},
				},
			],
			legend: this.defaultLegendOptions,
			plotOptions: {
				series: {
					marker: {
						enabled: m > 10,
						radius: 3.25,
						states: {
							hover: {
								radius: 6.5,
							},
						},
					},
					states: {
						hover: {
							halo: {
								size: 12,
							},
						},
					},
				},
				// halo is only meaningful for line/area markers, turn off for bars
				column: {
					states: {
						hover: {
							halo: null, // Highcharts reads haloOptions?.size, so null turns it off
						},
					},
				},
			},
		});

		if (hasSecondaryAxis) {
			options.yAxis.push(copyObject(options.yAxis[0]!));
			options.yAxis[1]!.labels!.x! *= -1;
			options.yAxis[1]!.labels!.align = 'right';
		}

		return options;
	}

	@cached
	get annotationsByAxisTs() {
		return this.getAnnotationsByAxisTsForSeries(this.seriesForGranulationLabels);
	}

	getMetricSelectionDataForAxisTs(ts: number): MetricSelectionData[] {
		const metricSelectionData: MetricSelectionData[] = [];

		const granulationLabels = this.seriesForGranulationLabels?.granulationLabels || [],
			label = granulationLabels.find((label) => label.ts === ts);

		if (label) {
			const index = granulationLabels.indexOf(label);
			this.series
				.filter((s) => s.isMain)
				.forEach((series) => {
					const point = series.points[index];
					if (point?.metricSelectionData) {
						// assumption is that the main series do not have the same data
						metricSelectionData.push(point.metricSelectionData);
					}
				});
		}

		return metricSelectionData;
	}

	protected formatStackTotal(total: number): string {
		const mainSeries = this.series.find((s) => s.isMain && s.isStacked);
		const firstPoint = mainSeries?.points.find((p) => p.hasValue);
		if (firstPoint?.format) {
			return formatNumber(total, firstPoint.format, { scale: firstPoint.scale });
		}
		return numeral(total).format('0.[0]a');
	}

	protected onChartLoad(chart: Highcharts.Chart) {
		if (this.annotationsHandler) {
			this.annotationsHandler.attachToChart(chart);
		}

		const tooltip = chart.tooltip;
		if (tooltip) {
			// Highcharts also calls tooltip.getPosition via .call() with a synthetic
			// context to position annotation labels (ControllableLabel#position) — we
			// must skip our intercept in that case, otherwise annotation labels drift
			// on redraw / resize.
			const protoGetPosition = (Object.getPrototypeOf(tooltip) as Highcharts.Tooltip)
				.getPosition;
			tooltip.getPosition = function (width, height, point) {
				if (this === tooltip && chart.hoverPoint?.options.id?.startsWith('anomaly-icon-')) {
					// outside: true is set on the base tooltip — Highcharts' default
					// getPosition bakes chart page offsets into its result, but when we
					// short-circuit with a chart-relative formula we have to add those
					// offsets ourselves so the tooltip lands in page coords.
					const { left, top } = chart.pointer.getChartPosition();
					const distance = this.options.distance!;

					// plotX is plot-area relative - add the chart's page offset and plotLeft
					// to land in page coords for horizontal centering.
					const pointX = left + chart.plotLeft + (point.plotX ?? 0);

					// keep within the viewport so the tooltip never renders outside bounds.
					// Highcharts sizes the outside container to the label plus this padding
					// (renderer.setSize in updatePosition), so the right bound must include it.
					const pad = (this.options.borderWidth ?? 0) + 2 * distance + 2;
					const x = Math.max(
						0,
						Math.min(
							pointX - width / 2,
							document.documentElement.clientWidth - width - pad,
						),
					);
					// y anchored to the data point (original behaviour)
					const y = (point.plotY ?? 0) - height + top - distance;

					return { x, y };
				}

				return protoGetPosition.call(this, width, height, point);
			};
		}
	}

	protected onChartRender(chart: ChartWithAnnotations) {
		if (this.annotationsHandler) {
			this.annotationsHandler.update();
		}
		if (this.hasLegend) {
			debounce(this, this.centerLegendItems, chart.legend, 500);
		}

		const anomalyAnnotations =
			chart.annotations?.filter(
				(a) => (a.userOptions as AnomalyAnnotationOptions).groupId === ANOMALY_GROUP_ID,
			) || [];

		(anomalyAnnotations as AnnotationtWithGraphicEl[]).forEach((a) => {
			this.updateAnomalyBarPosition(chart, a.graphic);
		});

		this.updateDynamicElementsOnRender(chart);
	}

	protected updateDynamicElementsOnRender(hChart: Highcharts.Chart) {
		let needsRedraw = false;
		hChart.series.forEach((s) => {
			// gradient-reverse class means our line's "threshold" is the one above the line, not below
			// so we must set it to the axis min/max value to correctly show it
			const dbxSeries = (s.userOptions as DChartInterface.SeriesOptions).dbxSeries;
			if (dbxSeries?.updateDynamicThresholdFrom) {
				const value = s.yAxis[dbxSeries.updateDynamicThresholdFrom];
				if ((s.options as Highcharts.SeriesAreaOptions).threshold !== value) {
					s.update({ threshold: value } as Highcharts.SeriesOptionsType, false);
					needsRedraw = true;
				}
				dbxSeries.updateDynamicThresholdFrom = null;
			}
		});
		if (needsRedraw) {
			hChart.redraw();
		}
	}

	protected initAnnotationsHandler(): void {
		// extended
	}

	protected destroyAnnotationsHandler(): void {
		if (this.annotationsHandler) {
			this.annotationsHandler.destroy();
		}
	}

	private updateAnomalyBarPosition(chart: ChartWithAnnotations, element: Highcharts.SVGElement) {
		// Run in next render, where state is up-to-date
		setTimeout(() => {
			const isEmptyObject = Object.keys(element).length === 0;
			if (!isEmptyObject) {
				chart.series.forEach((series) => {
					const isMain = (series.userOptions as DChartInterface.SeriesOptions).dbxSeries!
						.isMain;
					const isBar = (series.userOptions as DChartInterface.SeriesOptions).dbxSeries!
						.isBar;

					if ((series as SeriesWithColumnMetrics)?.columnMetrics && isMain && isBar) {
						element.translate(
							(series as SeriesWithColumnMetrics).columnMetrics!.offset +
								(series as SeriesWithColumnMetrics).columnMetrics!.width / 2,
							0,
						);
					}
				});
			}
		}, 0);
	}

	override updateSize(_hasModifierChanged: boolean): void {
		// update also when modifier is unchanged, only size changed bcos of defaultLegendOptions
		super.updateSize(true);
	}

	// eslint-disable-next-line ember/classic-decorator-hooks
	override destroy(): void {
		super.destroy();
		this.destroyAnnotationsHandler();
	}
}
